import { ScrollingModule } from '@angular/cdk/scrolling'
import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { BetaFeature, BetaFeaturesService } from '../../../../services/beta-features.service'
import { AlertService } from '../../../../v2/shared/services/alert.service'

const DELETE_FLAG_MODAL_ID = 'delete-feature-flag-modal'

@Component({
  selector: 'app-feature-flags',
  standalone: true,
  imports: [ReactiveFormsModule, ScrollingModule, NgClass, ModalComponent],
  templateUrl: './feature-flags.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureFlagsComponent implements OnInit {
  public featureFlags = signal<BetaFeature[]>([])
  public isLoading = signal(false)
  public isSaving = signal(false)
  public currentFlag = signal<BetaFeature | null>(null)
  public operationType = signal<'add' | 'edit' | 'delete' | null>(null)

  public isFormOpen = computed(() => (this.operationType() === 'add' && this.currentFlag() === null) || (this.operationType() === 'edit' && this.currentFlag() !== null))

  public flagForm: FormGroup
  public readonly deleteModalId = DELETE_FLAG_MODAL_ID
  private readonly _destroyedRef = inject(DestroyRef)

  constructor(private _betaFeaturesService: BetaFeaturesService, private _fb: FormBuilder, private _alertService: AlertService, private _modalService: ModalService) {}

  public ngOnInit(): void {
    this.flagForm = this._fb.group({
      flag: ['', [Validators.required, this._uniqueFlagValidator.bind(this)]],
      label: ['', Validators.required],
      description: ['', Validators.required],
      default: [false],
      permanent: [false]
    })
    this.loadFlags()
  }

  /**
   * Input event handler for the flag key input field.
   * Removes spaces and updates the form control value in-place.
   * @param event The input event.
   */
  public onFlagKeyInput(event: Event): void {
    const input = event.target as HTMLInputElement
    input.value = input.value.replace(/\s/g, '')
    this.flagForm.get('flag')?.setValue(input.value, { emitEvent: false })
  }

  /**
   * Custom validator to ensure the flag key is unique when adding a new flag.
   * @param control The AbstractControl for the flag input.
   * @returns A duplication error if a flag with this key exists, or null if unique.
   */
  private _uniqueFlagValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !this.operationType()) return null
    const exists = this.featureFlags().some((f) => f.flag === control.value)
    return exists ? { duplicate: true } : null
  }

  /**
   * Loads the current feature flag list from the BetaFeaturesService.
   * Shows a loading indicator during fetch.
   * Displays an error alert on failure.
   */
  public loadFlags(): void {
    this.isLoading.set(true)
    this._betaFeaturesService
      .reloadFeatures()
      .pipe(takeUntilDestroyed(this._destroyedRef))
      .subscribe({
        next: () => {
          this.featureFlags.set(this._betaFeaturesService.availableFeatures)
          this.isLoading.set(false)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message || 'Failed to load feature flags')
          this.isLoading.set(false)
        }
      })
  }

  /**
   * Starts adding a new feature flag.
   * Resets and enables the form for new input.
   */
  public onAdd(): void {
    this.operationType.set('add')
    this.currentFlag.set(null)
    this.flagForm.reset({ flag: '', label: '', description: '' })
    this.flagForm.get('flag')?.enable()
  }

  /**
   * Starts editing an existing feature flag.
   * Populates and disables the flag key in the form.
   * @param flag The flag configuration to edit.
   */
  public onEdit(flag: BetaFeature): void {
    this.operationType.set('edit')
    this.currentFlag.set(flag)
    this.flagForm.patchValue({
      flag: flag.flag,
      label: flag.label,
      description: flag.description,
      default: flag.default,
      permanent: flag.permanent
    })
    this.flagForm.get('flag')?.disable()
  }

  /**
   * Cancels the add/edit operation.
   * Resets and enables the feature flag form and clears edit/delete state.
   */
  public onCancel(operationType: 'add' | 'edit' | 'delete' | null): void {
    switch (operationType) {
      case 'delete':
        this.operationType.set(null)
        if (this._modalService.isModalOpen(DELETE_FLAG_MODAL_ID)) this._modalService.toggleModal(DELETE_FLAG_MODAL_ID)
        this.currentFlag.set(null)
        this.flagForm.reset()
        this.flagForm.get('flag')?.enable()
      default:
        this.operationType.set(null)
        this.currentFlag.set(null)
        this.flagForm.reset()
        this.flagForm.get('flag')?.enable()
        break
    }
  }

  /**
   * Handles the save action for adding or editing a flag.
   * Validates and collects form data, and updates the list accordingly.
   * Persists the updated list via the BetaFeaturesService.
   */
  public onSave(): void {
    if (this.flagForm.invalid) {
      this.flagForm.markAllAsTouched()
      return
    }
    this.isSaving.set(true)

    const flag = {
      flag: this.flagForm.getRawValue().flag,
      label: this.flagForm.value.label,
      description: this.flagForm.value.description,
      default: this.flagForm.value.default ?? false,
      permanent: this.flagForm.value.permanent ?? false,
      id: this.currentFlag()?.id
    }

    if (this.operationType() === 'add') {
      this._betaFeaturesService
        .addFeature(flag)
        .pipe(takeUntilDestroyed(this._destroyedRef))
        .subscribe({
          next: () => {
            this._alertService.alert(Alert.SUCCESS, 'Feature flag added successfully')
            this.onCancel('add')
            this._loadFlags()
            this.isSaving.set(false)
          },
          error: (error) => {
            this._alertService.alert(Alert.ERROR, error.error?.message || 'Failed to add feature flag')
            this.isSaving.set(false)
          }
        })
    } else if (this.operationType() === 'edit') {
      this._betaFeaturesService
        .editFeature(flag)
        .pipe(takeUntilDestroyed(this._destroyedRef))
        .subscribe({
          next: () => {
            this._alertService.alert(Alert.SUCCESS, 'Feature flag edited successfully')
            this.onCancel('edit')
            this._loadFlags()
            this.isSaving.set(false)
          },
          error: (error) => {
            this._alertService.alert(Alert.ERROR, error.error?.message || 'Failed to edit feature flag')
            this.isSaving.set(false)
          }
        })
    }
  }

  /**
   * Shows the delete confirmation prompt for a given flag.
   * @param flag The BetaFeatureConfig to potentially delete.
   */
  public onDeleteConfirm(flag: BetaFeature): void {
    this.currentFlag.set(flag)
    this._modalService.toggleModal(DELETE_FLAG_MODAL_ID)
  }

  /**
   * Finalizes deletion of a flag after confirmation.
   * Removes the flag from the local list and triggers save to backend.
   */
  public onDelete(): void {
    const flagToDelete = this.currentFlag()
    if (!flagToDelete) return this._modalService.toggleModal(DELETE_FLAG_MODAL_ID)

    this._betaFeaturesService
      .deleteFeature(flagToDelete.id)
      .pipe(takeUntilDestroyed(this._destroyedRef))
      .subscribe({
        next: () => {
          this._alertService.alert(Alert.SUCCESS, 'Feature flag deleted successfully')
          this.onCancel('delete')
          this._loadFlags()
          this.isSaving.set(false)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error.error?.message || 'Failed to delete feature flag')
          this.isSaving.set(false)
        }
      })
  }

  /**
   * Loads the feature flags from the API and updates the local state.
   * Sets saving indicator during fetch and handles errors gracefully.
   */
  private _loadFlags(): void {
    this.isSaving.set(true)
    this._betaFeaturesService
      .reloadFeatures()
      .pipe(takeUntilDestroyed(this._destroyedRef))
      .subscribe({
        next: () => {
          this.isSaving.set(false)
          this.featureFlags.set(this._betaFeaturesService.availableFeatures)
        },
        error: (error) => {
          this.isSaving.set(false)
          this._alertService.alert(Alert.ERROR, error.error?.message || 'Failed to load feature flags')
        }
      })
  }
}
